import {
  BottomSheetModal,
  BottomSheetScrollView,
  BottomSheetView,
} from '@gorhom/bottom-sheet'
import { useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { AssetGrid } from '../components/AssetGrid'
import { PillButton } from '../components/PillButton'
import { ScrollFade, useScrollEdgeFades } from '../components/ScrollFade'
import { TopBar } from '../components/TopBar'
import { FlowSheetBackground } from '../flow/FlowSheet'
import { sheetSizing, useInertScrollableFocus } from '../flow/sheetSizing'
import { useNestedSheet } from '../hooks/useNestedSheet'
import { ChainAvatar } from '../icons/ChainAvatar'
import { TokenChainIcon } from '../icons/TokenChainIcon'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'
import {
  ASSET_GRID_AVATAR_SIZE,
  ASSET_GRID_COLUMNS,
  gridCells,
} from '../utils/assetGrid'
import { FALLBACK_ASSET_LOGO } from '../icons/assetLogos'
import { footerBottomMargin } from '../utils/safeArea'
import { pickerChainForToken } from '../utils/tokenAndChainPicker'
import type {
  PickerChain,
  PickerSelection,
  PickerToken,
} from '../utils/tokenAndChainPicker'
import { SelectReceiveTokenScreen } from './SelectReceiveTokenScreen'

export interface TokenAndChainPickerScreenProps {
  title: string
  tokens: PickerToken[]
  chains: PickerChain[]
  initialSelection?: PickerSelection
  loading?: boolean
  onConfirm: (selection: PickerSelection) => void
  onBack: () => void
}

/**
 * Wrappers own the outer sheet frame. The full list overlays this screen so
 * its draft survives until Confirm commits or Back discards it.
 */
export function TokenAndChainPickerScreen({
  title,
  tokens,
  chains,
  initialSelection,
  loading = false,
  onConfirm,
  onBack,
}: TokenAndChainPickerScreenProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const { showTopFade, showBottomFade, scrollProps } = useScrollEdgeFades()
  const {
    nestedSheetRef,
    nestedScreen,
    setNestedScreen,
    openNestedScreen,
    closeNestedScreen,
    renderNestedBackdrop,
  } = useNestedSheet<'availableTokens'>()

  // Until the first pick the draft mirrors the committed pair, which can land
  // after mount (the withdrawal picker opens while its destinations still load).
  const [draft, setDraft] = useState<Draft>()
  const token = draft ? draft.symbol : initialSelection?.symbol
  const chainId = draft ? draft.chainId : initialSelection?.chainId
  // Only a pick from the full list moves this, so drafting around the grid can't
  // change which cells are on it.
  const [pinned, setPinned] = useState<string>()
  const pinnedToken = pinned ?? initialSelection?.symbol

  const symbols = tokens.map((entry) => entry.symbol)
  const selectedToken = tokens.find((entry) => entry.symbol === token)
  const canConfirm =
    !loading &&
    selectedToken !== undefined &&
    chainId !== undefined &&
    selectedToken.chainIds.includes(chainId) &&
    chains.some((entry) => Number(entry.id) === chainId)
  const { cells, overflowCount } = gridCells(symbols, TOKEN_CELLS, pinnedToken)

  // A token carries its chain: keep the drafted one if it can deliver.
  const draftToken = (next: string) => {
    setDraft((current) => ({
      symbol: next,
      chainId: pickerChainForToken(
        tokens,
        chains,
        next,
        current ? current.chainId : initialSelection?.chainId,
      ),
    }))
  }
  const draftChain = (next: number) => {
    setDraft((current) => ({
      symbol: current ? current.symbol : initialSelection?.symbol,
      chainId: next,
    }))
  }

  const footerBottom = footerBottomMargin(insets.bottom)
  // i18next picks the locale's own plural category from `count`, which is what
  // Russian needs to decline 2-4 differently from 5+.
  const moreLabel = t('selectReceiveToken.moreTokens', {
    count: overflowCount,
  })

  return (
    <Box
      style={{
        flex: 1,
        backgroundColor: theme.background.modal,
      }}
    >
      <TopBar title={title} onBack={onBack} />

      <View style={styles.scrollArea}>
        {/* gorhom's scrollable, not RN's — the sheet's pan gesture otherwise
            wins on Android. The step's sheet is pinned, so nothing depends on
            the content size it reports (see sheetSizing). */}
        <BottomSheetScrollView
          style={styles.scroll}
          contentContainerStyle={[
            styles.content,
            {
              paddingBottom:
                theme.sizes.buttonLg + CONFIRM_SCROLL_GAP + footerBottom,
            },
          ]}
          showsVerticalScrollIndicator={false}
          {...scrollProps}
        >
          <Box>
            <AssetGrid
              title={t('selectReceiveToken.selectToken')}
              items={(loading ? [] : cells).map((symbol) => ({
                id: symbol,
                label: symbol,
                icon: (
                  <TokenChainIcon
                    symbol={symbol}
                    size={ASSET_GRID_AVATAR_SIZE}
                  />
                ),
                selected: symbol === token,
                onPress: () => draftToken(symbol),
              }))}
              overflow={
                !loading && overflowCount > 0
                  ? {
                      label: moreLabel,
                      onPress: () => openNestedScreen('availableTokens'),
                      accessibilityLabel: moreLabel,
                    }
                  : undefined
              }
            />

            {loading || tokens.length === 0 ? (
              <Text
                variant="body"
                style={{ color: theme.content.elements.secondary }}
              >
                {loading ? t('common.loading') : t('selectAsset.empty')}
              </Text>
            ) : null}
          </Box>
          <Box>
            <AssetGrid
              title={t('selectReceiveToken.selectChain')}
              items={(loading || tokens.length === 0 ? [] : chains).map(
                (chain) => ({
                  id: chain.id,
                  label: chain.name,
                  icon: (
                    <ChainAvatar
                      uri={chain.iconUri ?? FALLBACK_ASSET_LOGO}
                      size={ASSET_GRID_AVATAR_SIZE}
                    />
                  ),
                  selected: Number(chain.id) === chainId,
                  unsupported:
                    !!token &&
                    !selectedToken?.chainIds.includes(Number(chain.id)),
                  onPress: () => draftChain(Number(chain.id)),
                }),
              )}
            />
            {loading || tokens.length === 0 || chains.length === 0 ? (
              <Text
                variant="body"
                style={{ color: theme.content.elements.secondary }}
              >
                {loading ? t('common.loading') : t('selectAsset.emptyChains')}
              </Text>
            ) : null}
          </Box>
        </BottomSheetScrollView>

        {showTopFade ? (
          <ScrollFade
            edge="top"
            color={theme.background.modal}
            id="receive-token-chain-grid"
          />
        ) : null}
        {showBottomFade ? (
          <ScrollFade
            edge="bottom"
            color={theme.background.modal}
            id="receive-token-chain-grid"
          />
        ) : null}

        {/* After the fades, so the button paints over them rather than under. */}
        <Box style={[styles.footer, { bottom: footerBottom }]}>
          <PillButton
            label={t('common.confirm')}
            disabled={!canConfirm}
            onPress={() => {
              if (canConfirm && token !== undefined && chainId !== undefined) {
                onConfirm({ chainId, symbol: token })
              }
            }}
          />
        </Box>
      </View>

      <BottomSheetModal
        ref={nestedSheetRef}
        // Minimizing the parent sheet swallows this nested sheet's taps on Android.
        stackBehavior="push"
        {...sheetSizing({ fullHeight: true })}
        topInset={insets.top}
        // No grabber on this screen — Close dismisses it, a drag shouldn't.
        enablePanDownToClose={false}
        handleComponent={null}
        backgroundComponent={FlowSheetBackground}
        backdropComponent={renderNestedBackdrop}
        onDismiss={() => setNestedScreen(null)}
      >
        <BottomSheetView focusHook={useInertScrollableFocus}>
          {nestedScreen === 'availableTokens' ? (
            <SelectReceiveTokenScreen
              tokens={symbols}
              selectedToken={token}
              onSelectToken={(next) => {
                draftToken(next)
                // Claims the grid's last cell, and keeps it.
                setPinned(next)
                closeNestedScreen()
              }}
              onClose={closeNestedScreen}
            />
          ) : null}
        </BottomSheetView>
      </BottomSheetModal>
    </Box>
  )
}

/** Two grid rows. */
const TOKEN_CELLS = ASSET_GRID_COLUMNS * 2

/** Either half may still be open: a chain pick before any token, or a token without a compatible chain. */
interface Draft {
  symbol?: string
  chainId?: number
}

const CONFIRM_SCROLL_GAP = 8

const styles = StyleSheet.create({
  scrollArea: { position: 'relative', flex: 1 },
  scroll: { flex: 1 },
  content: { paddingHorizontal: 24, paddingTop: 13, gap: 40 },
  footer: { position: 'absolute', left: 0, right: 0, paddingHorizontal: 24 },
})
